import React, { useState, useEffect, useRef } from 'react';
import { X, Shield, Loader2, ChevronDown, ChevronRight, AlertCircle, Check } from 'lucide-react';
import { Permission, PermissionDisplayNames, PermissionCategories, RoleDisplayNames, UserRole } from '@/types/global.types';
import { fetchStaffPermissions, updateStaffPermissions, type StaffPermissionsResponse } from '@/services/permissionsService';
import { useAuthStore } from '@/features/auth/stores/authStore';

interface StaffUser {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  email: string;
}

interface StaffPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  staff: StaffUser;
  onPermissionsUpdated?: () => void;
}

export const StaffPermissionsModal: React.FC<StaffPermissionsModalProps> = ({
  isOpen,
  onClose,
  staff,
  onPermissionsUpdated,
}) => {
  const currentUser = useAuthStore((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Permission data from API
  const [permissionData, setPermissionData] = useState<StaffPermissionsResponse | null>(null);

  // Local state for checked permissions
  const [checkedPermissions, setCheckedPermissions] = useState<Set<Permission>>(new Set());

  // Expanded categories state - all collapsed by default
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Ref to track scroll position
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  // Load permissions when modal opens
  useEffect(() => {
    if (isOpen && staff) {
      loadPermissions();
    }
  }, [isOpen, staff]);

  // Save scroll position whenever user scrolls
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      scrollPositionRef.current = container.scrollTop;
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  // Restore scroll position after state changes (permission toggles)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && scrollPositionRef.current > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        container.scrollTop = scrollPositionRef.current;
      });
    }
  }, [checkedPermissions, expandedCategories]);

  const loadPermissions = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchStaffPermissions(staff.id);
      setPermissionData(data);
      // Initialize checked permissions with effective permissions
      setCheckedPermissions(new Set(data.effectivePermissions));
    } catch (err) {
      // Show actual error message from backend if available
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du chargement des permissions';
      setError(errorMessage);
      console.error('Error loading permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Define permissions that depend on STUDENT_VIEW
  const studentDependentPermissions: Permission[] = [
    Permission.STUDENT_CREATE,
    Permission.STUDENT_BULK_CREATE,
    Permission.STUDENT_VALIDATE_MODIFICATIONS,
    Permission.STUDENT_EDIT_PROFILE,
    Permission.STUDENT_DELETE,
    Permission.STUDENT_ARCHIVE,
    Permission.STUDENT_SUSPEND_ACTIVATE,
    Permission.STUDENT_VALIDATE_PROFILE,
    Permission.STUDENT_RESET_PASSWORD,
    Permission.STUDENT_RESEND_ACTIVATION,
  ];

  // Define permissions that depend on FILIERE_VIEW
  const filiereDependentPermissions: Permission[] = [
    Permission.FILIERE_MANAGE,
  ];

  // Define permissions that depend on GROUP_VIEW
  const groupeDependentPermissions: Permission[] = [
    Permission.GROUP_MANAGE,
  ];

  // Define permissions that depend on STAFF_VIEW
  const staffDependentPermissions: Permission[] = [
    Permission.STAFF_CREATE,
    Permission.STAFF_BULK_CREATE,
    Permission.STAFF_BULK_CREATE_TEACHER,
    Permission.STAFF_VALIDATE_PROFILE,
    Permission.STAFF_VALIDATE_MODIFICATIONS,
    Permission.STAFF_EDIT_PROFILE,
    Permission.STAFF_DELETE,
    Permission.STAFF_ARCHIVE,
    Permission.STAFF_SUSPEND_ACTIVATE,
    Permission.STAFF_RESET_PASSWORD,
    Permission.STAFF_RESEND_ACTIVATION,
  ];

  // Define permissions that depend on FINANCE_PAYMENT_VIEW (for accessing payment page)
  const paymentPageDependentPermissions: Permission[] = [
    Permission.FINANCE_DISCOUNT_VIEW,    // Needs payment page to see discounts section
    Permission.FINANCE_DISCOUNT_APPROVE, // Needs payment page to see "Remises en Attente" tab
    Permission.FINANCE_TRANSFER_VIEW,    // Needs payment page to see transfers section
  ];

  // Define permissions that depend on FINANCE_DISCOUNT_VIEW
  const discountDependentPermissions: Permission[] = [
    Permission.FINANCE_DISCOUNT_MANAGE,  // Needs to view discounts to manage them
  ];

  // Define permissions that depend on BOTH FINANCE_DISCOUNT_VIEW AND FINANCE_DISCOUNT_MANAGE
  const discountViewAndManageDependentPermissions: Permission[] = [
    Permission.FINANCE_DISCOUNT_DELETE,  // Needs to view AND manage discounts to delete them
  ];

  // Define permissions that depend on BOTH FINANCE_TRANSFER_VIEW AND FINANCE_PAYMENT_MANAGE
  const transferApproveDependentPermissions: Permission[] = [
    Permission.FINANCE_TRANSFER_APPROVE, // Needs to view transfers AND manage payments to approve transfers
  ];

  // Define permissions that depend on FINANCE_TUITION_VIEW
  const tuitionDependentPermissions: Permission[] = [
    Permission.FINANCE_TUITION_MANAGE,
  ];

  // Define permissions that depend on FINANCE_ANCILLARY_VIEW
  const ancillaryDependentPermissions: Permission[] = [
    Permission.FINANCE_ANCILLARY_MANAGE,
  ];

  // Define permissions that depend on FINANCE_SCHEDULE_VIEW
  const scheduleDependentPermissions: Permission[] = [
    Permission.FINANCE_SCHEDULE_MANAGE,
  ];

  // Check if a permission is disabled due to missing prerequisite
  const isPermissionDisabledByDependency = (permission: Permission): boolean => {
    // Student permissions require STUDENT_VIEW
    if (studentDependentPermissions.includes(permission)) {
      return !checkedPermissions.has(Permission.STUDENT_VIEW);
    }
    // Staff permissions require STAFF_VIEW
    if (staffDependentPermissions.includes(permission)) {
      return !checkedPermissions.has(Permission.STAFF_VIEW);
    }
    // Filière permissions require FILIERE_VIEW
    if (filiereDependentPermissions.includes(permission)) {
      return !checkedPermissions.has(Permission.FILIERE_VIEW);
    }
    // Groupe permissions require GROUP_VIEW
    if (groupeDependentPermissions.includes(permission)) {
      return !checkedPermissions.has(Permission.GROUP_VIEW);
    }
    // Payment page permissions require FINANCE_PAYMENT_VIEW (to access the page)
    if (paymentPageDependentPermissions.includes(permission)) {
      return !checkedPermissions.has(Permission.FINANCE_PAYMENT_VIEW);
    }
    // Discount permissions require FINANCE_DISCOUNT_VIEW
    if (discountDependentPermissions.includes(permission)) {
      return !checkedPermissions.has(Permission.FINANCE_DISCOUNT_VIEW);
    }
    // Delete discount permission requires BOTH VIEW AND MANAGE
    if (discountViewAndManageDependentPermissions.includes(permission)) {
      return !checkedPermissions.has(Permission.FINANCE_DISCOUNT_VIEW) ||
             !checkedPermissions.has(Permission.FINANCE_DISCOUNT_MANAGE);
    }
    // Approve transfer permission requires BOTH VIEW transfers AND MANAGE payments
    if (transferApproveDependentPermissions.includes(permission)) {
      return !checkedPermissions.has(Permission.FINANCE_TRANSFER_VIEW) ||
             !checkedPermissions.has(Permission.FINANCE_PAYMENT_RECORD);
    }
    // Tuition MANAGE requires TUITION VIEW
    if (tuitionDependentPermissions.includes(permission)) {
      return !checkedPermissions.has(Permission.FINANCE_TUITION_VIEW);
    }
    // Ancillary MANAGE requires ANCILLARY VIEW
    if (ancillaryDependentPermissions.includes(permission)) {
      return !checkedPermissions.has(Permission.FINANCE_ANCILLARY_VIEW);
    }
    // Schedule MANAGE requires SCHEDULE VIEW
    if (scheduleDependentPermissions.includes(permission)) {
      return !checkedPermissions.has(Permission.FINANCE_SCHEDULE_VIEW);
    }
    return false;
  };

  // Get dependency message for a permission
  const getDependencyMessage = (permission: Permission): string | null => {
    if (studentDependentPermissions.includes(permission) && !checkedPermissions.has(Permission.STUDENT_VIEW)) {
      return 'Nécessite "Voir les étudiants"';
    }
    if (staffDependentPermissions.includes(permission) &&
        !checkedPermissions.has(Permission.STAFF_VIEW)) {
      return 'Nécessite "Voir le personnel"';
    }
    if (filiereDependentPermissions.includes(permission) &&
        !checkedPermissions.has(Permission.FILIERE_VIEW)) {
      return 'Nécessite "Voir les filières"';
    }
    if (groupeDependentPermissions.includes(permission) &&
        !checkedPermissions.has(Permission.GROUP_VIEW)) {
      return 'Nécessite "Voir les groupes"';
    }
    if (paymentPageDependentPermissions.includes(permission) &&
        !checkedPermissions.has(Permission.FINANCE_PAYMENT_VIEW)) {
      return 'Nécessite "Voir les paiements étudiants" (pour accéder à la page)';
    }
    if (discountDependentPermissions.includes(permission) &&
        !checkedPermissions.has(Permission.FINANCE_DISCOUNT_VIEW)) {
      return 'Nécessite "Voir les remises"';
    }
    if (discountViewAndManageDependentPermissions.includes(permission)) {
      if (!checkedPermissions.has(Permission.FINANCE_DISCOUNT_VIEW) &&
          !checkedPermissions.has(Permission.FINANCE_DISCOUNT_MANAGE)) {
        return 'Nécessite "Voir les remises" et "Gérer les remises"';
      }
      if (!checkedPermissions.has(Permission.FINANCE_DISCOUNT_VIEW)) {
        return 'Nécessite "Voir les remises"';
      }
      if (!checkedPermissions.has(Permission.FINANCE_DISCOUNT_MANAGE)) {
        return 'Nécessite "Gérer les remises"';
      }
    }
    if (transferApproveDependentPermissions.includes(permission)) {
      if (!checkedPermissions.has(Permission.FINANCE_TRANSFER_VIEW) &&
          !checkedPermissions.has(Permission.FINANCE_PAYMENT_RECORD)) {
        return 'Nécessite "Voir les virements" et "Enregistrer des paiements"';
      }
      if (!checkedPermissions.has(Permission.FINANCE_TRANSFER_VIEW)) {
        return 'Nécessite "Voir les virements"';
      }
      if (!checkedPermissions.has(Permission.FINANCE_PAYMENT_RECORD)) {
        return 'Nécessite "Enregistrer des paiements"';
      }
    }
    if (tuitionDependentPermissions.includes(permission) &&
        !checkedPermissions.has(Permission.FINANCE_TUITION_VIEW)) {
      return 'Nécessite "Voir la grille tarifaire"';
    }
    if (ancillaryDependentPermissions.includes(permission) &&
        !checkedPermissions.has(Permission.FINANCE_ANCILLARY_VIEW)) {
      return 'Nécessite "Voir les frais annexes"';
    }
    if (scheduleDependentPermissions.includes(permission) &&
        !checkedPermissions.has(Permission.FINANCE_SCHEDULE_VIEW)) {
      return 'Nécessite "Voir les échéanciers types"';
    }
    return null;
  };

  const handleTogglePermission = (permission: Permission) => {
    setCheckedPermissions(prev => {
      const newSet = new Set(prev);

      if (newSet.has(permission)) {
        // Unchecking a permission
        newSet.delete(permission);

        // If unchecking STUDENT_VIEW, also uncheck all dependent student permissions
        if (permission === Permission.STUDENT_VIEW) {
          studentDependentPermissions.forEach(p => newSet.delete(p));
        }

        // If unchecking STAFF_VIEW, uncheck all dependent staff permissions
        if (permission === Permission.STAFF_VIEW) {
          staffDependentPermissions.forEach(p => newSet.delete(p));
        }

        // If unchecking FILIERE_VIEW, uncheck all dependent filière permissions
        if (permission === Permission.FILIERE_VIEW) {
          filiereDependentPermissions.forEach(p => newSet.delete(p));
        }

        // If unchecking GROUP_VIEW, uncheck all dependent groupe permissions
        if (permission === Permission.GROUP_VIEW) {
          groupeDependentPermissions.forEach(p => newSet.delete(p));
        }

        // If unchecking FINANCE_PAYMENT_VIEW, uncheck all dependent payment page permissions
        if (permission === Permission.FINANCE_PAYMENT_VIEW) {
          paymentPageDependentPermissions.forEach(p => newSet.delete(p));
        }

        // If unchecking FINANCE_DISCOUNT_VIEW, uncheck all dependent discount permissions
        if (permission === Permission.FINANCE_DISCOUNT_VIEW) {
          discountDependentPermissions.forEach(p => newSet.delete(p));
          discountViewAndManageDependentPermissions.forEach(p => newSet.delete(p));
        }

        // If unchecking FINANCE_DISCOUNT_MANAGE, uncheck DELETE
        if (permission === Permission.FINANCE_DISCOUNT_MANAGE) {
          discountViewAndManageDependentPermissions.forEach(p => newSet.delete(p));
        }

        // If unchecking FINANCE_TRANSFER_VIEW, uncheck APPROVE
        if (permission === Permission.FINANCE_TRANSFER_VIEW) {
          transferApproveDependentPermissions.forEach(p => newSet.delete(p));
        }

        // If unchecking FINANCE_PAYMENT_RECORD (manage payments), uncheck TRANSFER_APPROVE
        if (permission === Permission.FINANCE_PAYMENT_RECORD) {
          transferApproveDependentPermissions.forEach(p => newSet.delete(p));
        }

        // If unchecking FINANCE_TUITION_VIEW, uncheck TUITION_MANAGE
        if (permission === Permission.FINANCE_TUITION_VIEW) {
          tuitionDependentPermissions.forEach(p => newSet.delete(p));
        }

        // If unchecking FINANCE_ANCILLARY_VIEW, uncheck ANCILLARY_MANAGE
        if (permission === Permission.FINANCE_ANCILLARY_VIEW) {
          ancillaryDependentPermissions.forEach(p => newSet.delete(p));
        }

        // If unchecking FINANCE_SCHEDULE_VIEW, uncheck SCHEDULE_MANAGE
        if (permission === Permission.FINANCE_SCHEDULE_VIEW) {
          scheduleDependentPermissions.forEach(p => newSet.delete(p));
        }
      } else {
        // Checking a permission
        newSet.add(permission);
      }

      return newSet;
    });
  };

  const handleToggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  // Ref to store timeout ID for cleanup
  const autoCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await updateStaffPermissions(staff.id, Array.from(checkedPermissions));
      setSuccessMessage('Permissions mises à jour avec succès');
      onPermissionsUpdated?.();

      // Auto-close after success with cleanup
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
      autoCloseTimeoutRef.current = setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError('Erreur lors de la mise à jour des permissions');
      console.error('Error updating permissions:', err);
    } finally {
      setSaving(false);
    }
  };

  const isPermissionFromRole = (permission: Permission): boolean => {
    return permissionData?.rolePermissions?.includes(permission) || false;
  };

  const isPermissionOverridden = (permission: Permission): boolean => {
    if (!permissionData) return false;
    return permissionData.overrides.some(o => o.permission === permission);
  };

  const hasChanges = (): boolean => {
    if (!permissionData) return false;
    const original = new Set(permissionData.effectivePermissions);
    if (original.size !== checkedPermissions.size) return true;
    for (const p of checkedPermissions) {
      if (!original.has(p)) return true;
    }
    return false;
  };

  // Check if the target user is a DG (Director General)
  // DG permissions are LOCKED and cannot be modified by anyone
  const isTargetDG = (): boolean => {
    return staff.role === UserRole.DIRECTOR_GENERAL;
  };

  // Check if current user can modify PERMISSION_MANAGE for this staff
  // Only DG can modify PERMISSION_MANAGE for Branch Managers and Directeur Pédagogique
  const canModifyPermissionManage = (): boolean => {
    if (!currentUser) return false;
    // DG permissions are locked
    if (isTargetDG()) return false;
    // Only DG can modify PERMISSION_MANAGE
    if (currentUser.role !== UserRole.DIRECTOR_GENERAL) return false;
    // Can only modify for Branch Managers and Directeur Pédagogique
    return staff.role === UserRole.BRANCH_MANAGER || staff.role === UserRole.PEDAGOGICAL_DIRECTOR;
  };

  // Check if a permission is permanently blocked for this role
  const isPermissionBlocked = (permission: Permission): boolean => {
    return permissionData?.blockedPermissions?.includes(permission) || false;
  };

  // Get blocked message for a permission
  const getBlockedMessage = (permission: Permission): string | null => {
    if (!isPermissionBlocked(permission)) return null;

    // Check for institution management permissions (blocked for TEACHER only)
    if (permission.startsWith('INSTITUTION_') || permission.startsWith('INFRASTRUCTURE_') ||
        permission.startsWith('DEPARTMENTS_') || permission.startsWith('OFFICIAL_DOCUMENTS_')) {
      if (staff.role === UserRole.TEACHER) {
        return 'Les professeurs n\'ont pas accès à la gestion de l\'établissement';
      }
      // Note: MARKETING_MANAGER and COMMERCIAL_MANAGER CAN have these permissions if granted
    }

    // E-DEP permissions blocked for specific roles
    if (permission.startsWith('EDEP_')) {
      if (staff.role === UserRole.MARKETING_MANAGER) {
        return 'Les responsables marketing n\'ont pas accès aux documents E-DEP';
      }
      if (staff.role === UserRole.COMMERCIAL_MANAGER) {
        return 'Les responsables commerciaux n\'ont pas accès aux documents E-DEP';
      }
    }

    // Settings permissions blocked for specific roles
    if (permission.startsWith('SETTINGS_USERS_')) {
      if (staff.role === UserRole.MARKETING_MANAGER) {
        return 'Les responsables marketing n\'ont pas accès aux paramètres utilisateurs';
      }
      if (staff.role === UserRole.COMMERCIAL_MANAGER) {
        return 'Les responsables commerciaux n\'ont pas accès aux paramètres utilisateurs';
      }
    }

    // PAYROLL permissions blocked for non-finance roles (FINANCE_* can be granted by DG)
    if (permission.startsWith('PAYROLL_') || permission === 'FINANCE_REPORT_PAYROLL') {
      if (staff.role === UserRole.PEDAGOGICAL_DIRECTOR) {
        return 'Les directeurs pédagogiques n\'ont pas accès à la paie';
      }
      if (staff.role === UserRole.MARKETING_MANAGER) {
        return 'Les responsables marketing n\'ont pas accès à la paie';
      }
      if (staff.role === UserRole.COMMERCIAL_MANAGER) {
        return 'Les responsables commerciaux n\'ont pas accès à la paie';
      }
      if (staff.role === UserRole.PROGRAM_HEAD) {
        return 'Les chefs de filière n\'ont pas accès à la paie';
      }
      if (staff.role === UserRole.ACADEMIC_COORDINATOR) {
        return 'Les coordinateurs académiques n\'ont pas accès à la paie';
      }
      if (staff.role === UserRole.TEACHER) {
        return 'Les professeurs n\'ont pas accès à la paie';
      }
    }

    // INSTITUTION_BANK permissions blocked for academic roles (only DG, BM, RF can access)
    if (permission.startsWith('INSTITUTION_BANK_')) {
      if (staff.role === UserRole.PEDAGOGICAL_DIRECTOR) {
        return 'Les directeurs pédagogiques n\'ont pas accès aux informations bancaires';
      }
      if (staff.role === UserRole.PROGRAM_HEAD) {
        return 'Les chefs de filière n\'ont pas accès aux informations bancaires';
      }
      if (staff.role === UserRole.ACADEMIC_COORDINATOR) {
        return 'Les coordinateurs académiques n\'ont pas accès aux informations bancaires';
      }
      if (staff.role === UserRole.MARKETING_MANAGER) {
        return 'Les responsables marketing n\'ont pas accès aux informations bancaires';
      }
      if (staff.role === UserRole.COMMERCIAL_MANAGER) {
        return 'Les responsables commerciaux n\'ont pas accès aux informations bancaires';
      }
      if (staff.role === UserRole.TEACHER) {
        return 'Les professeurs n\'ont pas accès aux informations bancaires';
      }
    }

    // All finance permissions blocked for students and parents
    if (permission.startsWith('FINANCE_') || permission.startsWith('INSTITUTION_BANK_') || permission.startsWith('PAYROLL_')) {
      if (staff.role === UserRole.STUDENT) {
        return 'Les étudiants n\'ont pas accès à la gestion financière';
      }
      if (staff.role === UserRole.PARENT) {
        return 'Les parents n\'ont pas accès à la gestion financière';
      }
    }

    // Provide role-specific explanations
    if (staff.role === UserRole.TEACHER) {
      if (permission.startsWith('STAFF_')) {
        return 'Les professeurs ne peuvent pas gérer le personnel';
      }
      if (permission.startsWith('STUDENT_')) {
        return 'Les professeurs ne peuvent pas créer/modifier/supprimer des étudiants';
      }
      if (permission === Permission.PERMISSION_MANAGE) {
        return 'Les professeurs ne peuvent pas gérer les permissions';
      }
      if (permission.startsWith('SETTINGS_USERS_')) {
        return 'Les professeurs ne peuvent pas gérer les paramètres utilisateurs';
      }
      if (permission.startsWith('EDEP_')) {
        return 'Les professeurs ne peuvent pas gérer les documents E-DEP';
      }
    }

    if (staff.role === UserRole.COMMERCIAL_MANAGER) {
      if (permission.startsWith('STAFF_')) {
        return 'Les responsables commerciaux ne peuvent pas gérer le personnel';
      }
      if (permission === Permission.PERMISSION_MANAGE) {
        return 'Les responsables commerciaux ne peuvent pas gérer les permissions';
      }
    }

    if (staff.role === UserRole.MARKETING_MANAGER) {
      if (permission.startsWith('STAFF_')) {
        return 'Les responsables marketing ne peuvent pas gérer le personnel';
      }
      if (permission === Permission.PERMISSION_MANAGE) {
        return 'Les responsables marketing ne peuvent pas gérer les permissions';
      }
    }

    return 'Permission non disponible pour ce rôle';
  };

  // Check if a permission can be modified
  const canModifyPermission = (permission: Permission): boolean => {
    // DG permissions are ALL LOCKED - cannot be modified by anyone
    if (isTargetDG()) {
      return false;
    }

    // BLOCKED permissions cannot be modified by ANYONE (including DG)
    if (isPermissionBlocked(permission)) {
      return false;
    }

    // PERMISSION_MANAGE has special rules
    if (permission === Permission.PERMISSION_MANAGE) {
      return canModifyPermissionManage();
    }

    // Directeur Pédagogique can modify permissions for its academic subordinates
    // (Chef de Filière, Coordinateur Académique, Professeur). Campus scoping is
    // enforced server-side. Financial / payroll / bank permissions stay DG-only.
    if (currentUser?.role === UserRole.PEDAGOGICAL_DIRECTOR) {
      const dpSubordinateRoles = [
        UserRole.PROGRAM_HEAD,
        UserRole.ACADEMIC_COORDINATOR,
        UserRole.TEACHER,
      ];
      if (!dpSubordinateRoles.includes(staff.role as UserRole)) return false;
      const financialPrefixes = ['FINANCE_', 'PAYROLL_', 'INSTITUTION_BANK_'];
      if (financialPrefixes.some((p) => permission.startsWith(p))) return false;
      return true;
    }

    // For student permissions: check if current user can modify them
    if (permission.startsWith('STUDENT_')) {
      // DG can modify all student permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify student permissions for subordinates
      // But NOT for other Branch Managers or DG
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // For staff permissions: check if current user can modify them
    if (permission.startsWith('STAFF_')) {
      // DG can modify all staff permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify staff permissions for subordinates
      // But NOT for other Branch Managers or DG
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // For team permissions: check if current user can modify them
    if (permission.startsWith('TEAM_')) {
      // DG can modify all team permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify team permissions for subordinates
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // For tracking & reports permissions: check if current user can modify them
    if (permission.startsWith('TRACKING_') || permission.startsWith('EDEP_')) {
      // DG can modify all tracking/reports permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify tracking/reports permissions for subordinates
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // For user settings permissions: check if current user can modify them
    if (permission.startsWith('SETTINGS_USERS_')) {
      // DG can modify all settings permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify settings permissions for subordinates
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // Legacy reports permissions (for backwards compatibility)
    if (permission.startsWith('REPORTS_')) {
      // DG can modify all reports permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify reports permissions for subordinates
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // For institution management permissions: check if current user can modify them
    if (permission.startsWith('INSTITUTION_') || permission.startsWith('INFRASTRUCTURE_') ||
        permission.startsWith('DEPARTMENTS_') || permission.startsWith('OFFICIAL_DOCUMENTS_')) {
      // DG can modify all institution permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify institution permissions for subordinates
      // But NOT for other Branch Managers or DG
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // For pedagogique permissions (filières, groupes): check if current user can modify them
    if (permission.startsWith('FILIERE_') || permission.startsWith('GROUP_')) {
      // DG can modify all pedagogique permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify pedagogique permissions for subordinates
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // For academic config permissions: check if current user can modify them
    if (permission.startsWith('ACADEMIC_CONFIG_')) {
      // DG can modify all academic config permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify academic config permissions for subordinates
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // For request/attestation permissions: check if current user can modify them
    if (permission.startsWith('REQUEST_')) {
      // DG can modify all request permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify request permissions for subordinates
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // For calendar & planning permissions: check if current user can modify them
    if (permission.startsWith('CALENDAR_') || permission.startsWith('SCHEDULE_') ||
        permission.startsWith('EXAM_SCHEDULE_') || permission.startsWith('RATTRAPAGE_SCHEDULE_') ||
        permission === Permission.SESSION_CANCEL) {
      // DG can modify all calendar permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify calendar permissions for subordinates
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // For Notes & Evaluation permissions: check if current user can modify them
    if (permission.startsWith('NOTES_') || permission.startsWith('ATTENDANCE_') || permission.startsWith('JURY_')) {
      // DG can modify all Notes & Evaluation permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify Notes & Evaluation permissions for subordinates
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // For Qualité & Suivi permissions: check if current user can modify them
    if (permission.startsWith('PROFESSOR_MONITORING_') || permission.startsWith('EVALUATION_')) {
      // DG can modify all Qualité & Suivi permissions for any subordinate
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return true;
      }
      // Branch Manager with PERMISSION_MANAGE can modify Qualité & Suivi permissions for subordinates
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role !== UserRole.BRANCH_MANAGER) {
        return true;
      }
      return false;
    }

    // For Financial permissions (Payments, Tuition, Institution Bank): check if current user can modify them
    // DG can modify for many roles (DP, CF, AC, RM, RC, BM, FM)
    if (permission.startsWith('FINANCE_') || permission.startsWith('INSTITUTION_BANK_')) {
      // DG can modify financial permissions for all staff roles (except DG itself)
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        // Allow for BM, FM, DP, CF, AC, RM, RC
        return [
          UserRole.BRANCH_MANAGER,
          UserRole.FINANCIAL_MANAGER,
          UserRole.PEDAGOGICAL_DIRECTOR,
          UserRole.PROGRAM_HEAD,
          UserRole.ACADEMIC_COORDINATOR,
          UserRole.MARKETING_MANAGER,
          UserRole.COMMERCIAL_MANAGER,
        ].includes(staff.role as UserRole);
      }
      // Branch Manager can modify financial permissions for Financial Manager
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role === UserRole.FINANCIAL_MANAGER) {
        return true;
      }
      return false;
    }

    // For Payroll permissions: check if current user can modify them
    // DG can modify for Branch Manager and Financial Manager
    if (permission.startsWith('PAYROLL_')) {
      // DG can modify all payroll permissions for Branch Manager and Financial Manager
      if (currentUser?.role === UserRole.DIRECTOR_GENERAL) {
        return staff.role === UserRole.BRANCH_MANAGER || staff.role === UserRole.FINANCIAL_MANAGER;
      }
      // Branch Manager can modify payroll permissions for Financial Manager
      if (currentUser?.role === UserRole.BRANCH_MANAGER && staff.role === UserRole.FINANCIAL_MANAGER) {
        return true;
      }
      return false;
    }

    // Other permission categories not yet functional
    return false;
  };

  // Get category status: 'full' | 'partial' | 'none'
  const getCategoryStatus = (permissions: Permission[]): 'full' | 'partial' | 'none' => {
    const checkedCount = permissions.filter(p => checkedPermissions.has(p)).length;
    if (checkedCount === permissions.length) return 'full';
    if (checkedCount > 0) return 'partial';
    return 'none';
  };

  if (!isOpen) return null;

  const roleDisplayName = RoleDisplayNames[staff.role as UserRole] || staff.role;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden z-10">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield size={24} className="text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Gérer les permissions
                </h2>
                <p className="text-sm text-gray-500">
                  {staff.firstName} {staff.lastName} • {roleDisplayName}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div ref={scrollContainerRef} className="px-6 py-4 overflow-y-auto max-h-[60vh]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={32} className="animate-spin text-primary-600" />
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">{error}</p>
                  <button
                    onClick={loadPermissions}
                    className="text-sm text-red-600 hover:text-red-700 mt-1"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Info banner - different for DG (locked) vs other roles */}
                {isTargetDG() ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-start gap-3">
                    <Shield size={18} className="text-purple-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-purple-800">
                      Le <strong>Directeur Général</strong> dispose de toutes les permissions par défaut.
                      Ces permissions sont <strong>verrouillées</strong> et ne peuvent pas être modifiées.
                    </p>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
                    <AlertCircle size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-800">
                      Les permissions cochées par défaut proviennent du rôle <strong>{roleDisplayName}</strong>.
                      Décocher une permission la retire explicitement pour cet utilisateur.
                    </p>
                  </div>
                )}

                {/* Permission categories */}
                {Object.entries(PermissionCategories).map(([key, category]) => {
                  const isExpanded = expandedCategories.has(key);
                  const status = getCategoryStatus(category.permissions);
                  const isFunctional = key === 'institution' || key === 'student' || key === 'staff' || key === 'team' || key === 'trackingReports' || key === 'settings' || key === 'pedagogique' || key === 'calendar' || key === 'notesEvaluation' || key === 'qualiteSuivi' || key === 'financeModule' || key === 'payroll' || key === 'financeReporting';

                  return (
                    <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Category header */}
                      <button
                        onClick={() => handleToggleCategory(key)}
                        className={`w-full px-4 py-3 flex items-center justify-between text-left transition-colors ${
                          isFunctional ? 'hover:bg-gray-50' : 'bg-gray-50 cursor-default'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown size={18} className="text-gray-400" />
                          ) : (
                            <ChevronRight size={18} className="text-gray-400" />
                          )}
                          <span className={`font-medium ${isFunctional ? 'text-gray-900' : 'text-gray-500'}`}>
                            {category.label}
                          </span>
                          {!isFunctional && (
                            <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">
                              Bientôt disponible
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {status === 'full' && (
                            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                              Toutes
                            </span>
                          )}
                          {status === 'partial' && (
                            <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
                              Partiel
                            </span>
                          )}
                          {status === 'none' && (
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                              Aucune
                            </span>
                          )}
                        </div>
                      </button>

                      {/* Category permissions */}
                      {isExpanded && (
                        <div className="px-4 py-3 border-t border-gray-100 bg-white space-y-2">
                          {category.permissions.map((permission) => {
                            const isChecked = checkedPermissions.has(permission);
                            const isFromRole = isPermissionFromRole(permission);
                            const isOverridden = isPermissionOverridden(permission);
                            const canModify = canModifyPermission(permission);
                            const isDisabledByDependency = isPermissionDisabledByDependency(permission);
                            const dependencyMessage = getDependencyMessage(permission);
                            const blocked = isPermissionBlocked(permission);
                            const blockedMessage = getBlockedMessage(permission);
                            const isFullyDisabled = !canModify || isDisabledByDependency || blocked;


                            return (
                              <label
                                key={permission}
                                className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                                  blocked
                                    ? 'cursor-not-allowed opacity-40 bg-gray-50'
                                    : isFullyDisabled
                                      ? 'cursor-not-allowed opacity-60'
                                      : 'cursor-pointer hover:bg-gray-50'
                                }`}
                                title={blockedMessage || dependencyMessage || undefined}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => !isFullyDisabled && handleTogglePermission(permission)}
                                  disabled={isFullyDisabled}
                                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className={`text-sm ${blocked ? 'text-gray-400 line-through' : isFullyDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
                                      {PermissionDisplayNames[permission]}
                                    </span>
                                    {blocked && (
                                      <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                                        bloqué
                                      </span>
                                    )}
                                  </div>
                                  {blocked && blockedMessage && (
                                    <p className="text-xs text-red-600 mt-0.5">{blockedMessage}</p>
                                  )}
                                  {!blocked && isFromRole && !isOverridden && (
                                    <span className="ml-2 text-xs text-gray-400">(défaut du rôle)</span>
                                  )}
                                  {!blocked && isOverridden && (
                                    <span className={`ml-2 text-xs ${isChecked ? 'text-green-600' : 'text-red-600'}`}>
                                      ({isChecked ? 'accordée' : 'retirée'})
                                    </span>
                                  )}
                                  {!blocked && dependencyMessage && (
                                    <p className="text-xs text-orange-600 mt-0.5">{dependencyMessage}</p>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            {successMessage && (
              <div className="mb-3 flex items-center gap-2 text-sm text-green-600">
                <Check size={16} />
                {successMessage}
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving || loading || !hasChanges() || isTargetDG()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                title={isTargetDG() ? 'Les permissions du DG sont verrouillées' : ''}
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Enregistrement...</span>
                  </>
                ) : (
                  <>
                    <Shield size={16} />
                    <span>{isTargetDG() ? 'Verrouillé' : 'Enregistrer'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffPermissionsModal;
